import { connect } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { useListSearch } from "../../../common/hooks";
import TooltipButton from "../../../common/tooltipButton";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import { useIntl } from "react-intl";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import {
  listRequest,
  setQuery,
  userListRequest,
  exportRequest,
} from "../../../../store/slices/rolesSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import RoleUserList from "./RoleUserList";

export const RoleList = ({
  action,
  labels,
  list,
  listRequest,
  query,
  requesting,
  setQuery,
  totalCount,
  user,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [isUserListDialogVisible, setUserListDialogVisible] = useState(false);
  const [namePattern, setNamePattern] = useListSearch(
    query?.namePattern,
    (value) => setQuery({ namePattern: value, pageNo: 1 })
  );
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.UserAccess,
    AccessScopeConstants.CreateScope
  );

  const fetchList = useCallback(() => {
    listRequest({
      ...query,
      sortColumn: query.sortColumn || "name",
    });
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onSelectionChange = (selectedRecords) => {
    setSelectedRecords(selectedRecords?.value || []);
  };

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({
        ...query,
        pageNo: pageData.page + 1,
        pageSize: pageData.rows,
      });
    }
  };

  const handleOnSortChange = (sortData) => {
    setQuery({
      ...query,
      pageNo: 1,
      sortColumn: sortData.sortField,
      sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
    });
  };

  const handleAddRole = () => {
    navigate(RouteConstants.APP_ROUTE_ADMINISTRATION_ROLES_ADD);
  };

  const onUserList = (id) => {
    setSelectedId(id);
    setUserListDialogVisible(true);
  };

  return (
    <div className="pb-8 md: pb-0">
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "roles" })}
          </div>
          <div className="edexlms-page-actions">
            <TooltipButton
              icon="pi pi-plus"
              onClick={handleAddRole}
              size="small"
              label={intl.formatMessage({ id: "add_role" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({ id: "search_roles" })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
          </div>
          <List
            loading={action === "list" && requesting}
            onSelectionChange={onSelectionChange}
            handlePageChange={handlePageChange}
            handleOnSortChange={handleOnSortChange}
            data={list || []}
            totalRecords={totalCount || 0}
            handleRowClick={(data) =>
              navigate(
                `${RouteConstants.APP_ROUTE_ADMINISTRATION_ROLES_EDIT.replace(
                  ":id",
                  data?.data?.id
                )}`
              )
            }
            selectedRecords={selectedRecords}
            pageQuery={query}
            selectionMode=""
            columns={[
              {
                field: "name",
                header: "name",
                sortable: true,
              },
              {
                field: "",
                header: "user_scope",
                sortable: false,
                infoMessage: [
                  "administration",
                  "custom_fields",
                  "audiences",
                  "assignments",
                ],
                body: ListCellRenders.Permission("UserAccess"),
              },
              {
                field: "",
                header: "enterprise_scope",
                sortable: false,
                infoMessage: [
                  "notifications",
                  "enterprise_management",
                ],
                body: ListCellRenders.Permission("EnterpriseAccess"),
              },
              {
                field: "",
                header: "content_scope",
                sortable: false,
                infoMessage: [
                  "learning_content",
                  "assignments",
                  "certificates",
                ],
                body: ListCellRenders.Permission("ContentAccess"),
              },
              {
                field: "",
                header: "report_scope",
                sortable: false,
                infoMessage: [
                  "reports",
                  "saved_reports",
                  "certificate_batch_downloads",
                ],
                body: ListCellRenders.Permission("ReportAccess"),
              },
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions([
                  {
                    label: "view_users",
                    icon: "pi pi-users",
                    isAllowed: true,
                    action: (data) => onUserList(data.id),
                  },
                ]),
              },
            ]}
          />
        </div>
      </div>
      <Dialog
        style={{
          width: "70vw",
          minWidth: "800px",
          height: "80vh",
          minHeight: "500px",
        }}
        visible={isUserListDialogVisible}
        onHide={() => setUserListDialogVisible(false)}
        header={intl.formatMessage(
          { id: "role_users" },
          { name: list.find((a) => a.id === selectedId)?.name }
        )}
      >
        <RoleUserList roleId={selectedId} />
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state?.roles?.data?.list || [],
  totalCount: state?.roles?.data?.totalCount,
  action: state?.roles?.action,
  requesting: state?.roles?.requesting,
  error: state?.roles?.error,
  query: state?.roles?.query,
  user: state?.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(listRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
  userListRequest: (data) => dispatch(userListRequest(data)),
  exportRequest: (data) => dispatch(exportRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleList);
